import { useEffect, useRef, useState } from 'react';

/**
 * The instruction film a candidate must watch before a section begins.
 *
 * Compulsory and unskippable, which is the whole reason it exists as its own
 * component rather than a `<video controls>`. Native controls hand over a
 * scrubber, and a scrubber is a skip button; the browser's own menu also
 * offers playback speed, which is a fast-forward by another name. So the
 * element is given no controls at all and the transport is rebuilt here with
 * only the parts a candidate is allowed:
 *
 *   - play and pause, because stopping to listen again is not skipping
 *   - a progress bar that reports, and cannot be dragged
 *
 * Seeking forward is undone rather than merely hidden: anything that moves the
 * playhead past the furthest point actually watched — a media key, a gesture,
 * an extension, a script in the console — is snapped back. Playback speed is
 * forced to 1 for the same reason. Watching it once through is the only way to
 * reach the end, and reaching the end is what unlocks the section.
 */

interface Props {
  src: string;
  mimeType: string | null;
  onWatched: () => void;
  /**
   * Whether the centre has made watching this optional.
   *
   * It changes one sentence and nothing else. The transport stays as it is —
   * a centre saying the film need not be watched is not asking for a
   * fast-forward through the half of it a candidate does watch — but the line
   * under the frame cannot go on saying it cannot be skipped while a button
   * offering exactly that sits beneath it.
   */
  optional?: boolean;
}

/** How tall the frame may get. Mirrored in the width cap, so the two agree. */
const FRAME_CAP = 'min(68vh, 620px)';

/** `HTMLMediaElement.HAVE_METADATA` — the film's shape and length are known. */
const HAVE_METADATA = 1;

const format = (s: number) => {
  if (!Number.isFinite(s)) return '--:--';
  const m = Math.floor(s / 60);
  const rest = Math.floor(s % 60);
  return `${m}:${String(rest).padStart(2, '0')}`;
};

export function InstructionVideo({ src, mimeType, onWatched, optional = false }: Props) {
  const media = useRef<HTMLVideoElement>(null);
  /** The furthest point genuinely reached, which is the seek ceiling. */
  const watched = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(0);
  const [blocked, setBlocked] = useState(false);
  /**
   * The film's own shape, once the browser knows it.
   *
   * Without it the frame is a fixed box the picture is fitted inside, and
   * anything that is not the shape of that box is padded with black down both
   * sides — 85 pixels of it for a 4:3 film, 237 for a vertical one. Sizing the
   * frame to the film instead means there is nothing left over to pad.
   */
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    const el = media.current;
    if (!el) return;

    const onTime = () => {
      watched.current = Math.max(watched.current, el.currentTime);
      setAt(el.currentTime);
    };

    // A jump past what has actually been watched is undone. Backwards is
    // allowed: hearing something a second time is not a shortcut.
    const onSeeking = () => {
      if (el.currentTime > watched.current + 0.5) el.currentTime = watched.current;
    };

    const onRate = () => {
      if (el.playbackRate !== 1) el.playbackRate = 1;
    };

    const onLoaded = () => {
      setLength(el.duration);
      // An audio-only instruction reports no picture; it has no shape to take.
      setRatio(el.videoWidth > 0 && el.videoHeight > 0 ? el.videoWidth / el.videoHeight : null);
    };
    const onEnded = () => {
      setPlaying(false);
      onWatched();
    };

    // The browser may already know the film before this effect runs — on a
    // reload it is in the cache and `loadedmetadata` has fired before React
    // attached anything. Missing it left the frame without the film's shape,
    // which is the fallback that puts black down both sides.
    if (el.readyState >= HAVE_METADATA) onLoaded();

    el.addEventListener('timeupdate', onTime);
    el.addEventListener('seeking', onSeeking);
    el.addEventListener('seeked', onSeeking);
    el.addEventListener('ratechange', onRate);
    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('seeking', onSeeking);
      el.removeEventListener('seeked', onSeeking);
      el.removeEventListener('ratechange', onRate);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('ended', onEnded);
    };
  }, [onWatched]);

  const toggle = () => {
    const el = media.current;
    if (!el) return;
    if (el.paused) {
      void el
        .play()
        .then(() => {
          setPlaying(true);
          setBlocked(false);
        })
        .catch(() => setBlocked(true));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const isAudioOnly = mimeType?.startsWith('audio/') ?? false;
  const progress = length > 0 ? Math.min(100, (at / length) * 100) : 0;

  return (
    <div className="watch">
      <video
        ref={media}
        src={src}
        preload="metadata"
        playsInline
        className={`watch__screen ${isAudioOnly ? 'watch__screen--audio' : ''}`}
        style={
          ratio
            ? {
                // Fills the column, and keeps the film's shape while doing it.
                width: '100%',
                aspectRatio: String(ratio),
                // Never taller than the cap, never wider than the column, and
                // always the film's own shape — so no black is ever shown.
                maxWidth: `min(100%, calc(${FRAME_CAP} * ${ratio}))`,
              }
            : undefined
        }
        onContextMenu={(e) => e.preventDefault()}
      />

      <div className="watch__bar">
        <button type="button" className="btn" onClick={toggle}>
          {playing ? 'Pause' : at > 0 ? 'Continue' : 'Play'}
        </button>

        {/* Reports progress; deliberately not a control. */}
        <div
          className="watch__track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <span className="watch__fill" style={{ width: `${progress}%` }} />
        </div>

        <span className="mono watch__time">
          {format(at)} / {format(length)}
        </span>
      </div>

      {blocked && (
        <p className="field__hint">
          Your browser stopped the video from starting. Press Play again.
        </p>
      )}
      <p className="field__hint">
        {optional
          ? 'This plays once through and cannot be sped up. Your centre does not require you to watch it.'
          : 'This plays once through. It cannot be skipped or sped up.'}
      </p>
    </div>
  );
}
